import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Settings, Trash2, Bot, AlertCircle } from 'lucide-react';
import BrandIcon from '../components/BrandIcon';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import AgentForm from '../components/AgentForm';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle, Card, Badge, Button,
  Flex, Grid, ModalOverlay, ModalBox,
  ModalHeader, ModalTitle, ModalBody, ModalFooter, EmptyState, EmptyIcon, EmptyText,
  LoadingContainer, Spinner, Label, FormGroup,
  SectionTitle, FieldHint,
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const CATEGORY_LABELS = {
  pessoal: 'Pessoal', atendimento: 'Atendimento', vendas: 'Vendas',
  suporte: 'Suporte', marketing: 'Marketing', financeiro: 'Financeiro',
  operacoes: 'Operações', juridico: 'Jurídico', rh: 'RH', ti: 'TI',
  saude: 'Saúde', imobiliario: 'Imobiliário', ecommerce: 'E-commerce',
  produtividade: 'Produtividade', geral: 'Geral', interno: 'Interno',
};
const CATEGORY_ORDER = [
  'saude','atendimento','vendas','suporte','marketing','financeiro',
  'operacoes','juridico','rh','ti','produtividade','imobiliario',
  'ecommerce','pessoal','geral','interno',
];

const TenantAgents = () => {
  const { t, i18n } = useTranslation(['agents', 'marketplace']);
  const toast   = useToast();
  const confirm = useConfirm();
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('agents');
  const [installed, setInstalled]   = useState([]);
  const [available, setAvailable]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [configuring, setConfiguring] = useState(null);
  const [configuringInitialTab, setConfiguringInitialTab] = useState('identity');
  const [installing, setInstalling] = useState(null);
  const [uninstallingId, setUninstallingId] = useState(null);
  const [formData, setFormData]     = useState({});
  const [connectorsByAgent, setConnectorsByAgent] = useState({});

  const [allConnectorTypes, setAllConnectorTypes] = useState([]);
  const [agentErrors, setAgentErrors] = useState([]);
  const [tenantCredentials, setTenantCredentials] = useState([]);
  const [connectorTemplates, setConnectorTemplates] = useState([]);
  const [mediaFeatures, setMediaFeatures] = useState({ vision: false, audio: false, documents: false });
  const [availSearch, setAvailSearch] = useState('');
  const [availCategory, setAvailCategory] = useState('');
  const [availTag, setAvailTag] = useState('');
  const tenantPublicSlugRef = React.useRef('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  const breadcrumbItems = [
    isSallusAdmin
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : user.role === 'manager'
        ? { label: t('home', { ns: 'common' }), path: `/manager/${tenantId}` }
        : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` },
    { label: t('title'), icon: '🤖' }
  ];

  useEffect(() => { loadAll(); }, [tenantId, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    try {
      setLoading(true);
      const lang = i18n.language;
      const [installedRes, marketRes, tenantRes, connectorsRes, connectorTypesRes] = await Promise.all([
        api.get(`/marketplace/tenants/${tenantId}/agents`),
        api.get(`/marketplace/agents?lang=${lang}`),
        api.get(`/tenants/${tenantId}`),
        api.get(`/connectors/${tenantId}`),
        api.get('/marketplace/connector-types'),
      ]);
      const inst = installedRes.data.agents || [];
      setInstalled(inst);
      setAvailable(marketRes.data.agents || []);
      tenantPublicSlugRef.current = tenantRes.data?.publicSlug || '';

      // Resolve media features from tier + addons
      const tenantAddons = tenantRes.data?.addOns || [];
      try {
        const tiersRes = await api.get(`/platform/tiers?tenantId=${tenantId}`);
        const tiers = tiersRes.data.tiers || [];
        const tierObj = tiers.find(ti => ti.tierId === tenantRes.data?.tier);
        const features = tierObj?.features || {};
        setMediaFeatures({
          vision: features.vision === true || tenantAddons.includes('vision_extra'),
          audio: features.audio === true || tenantAddons.includes('audio_extra'),
          documents: features.documents === true || tenantAddons.includes('documents_extra'),
        });
      } catch { setMediaFeatures({ vision: false, audio: false, documents: false }); }

      const allConnectors = connectorsRes.data.connectors || [];
      const byAgent = {};
      for (const c of allConnectors) {
        if (c.agentInstanceId) {
          byAgent[c.agentInstanceId] = byAgent[c.agentInstanceId] || [];
          byAgent[c.agentInstanceId].push(c);
        }
      }
      setConnectorsByAgent(byAgent);
      setAllConnectorTypes(connectorTypesRes.data.connectorTypes || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // Dados secundários em background — não bloqueiam o render
    Promise.all([
      api.get(`/marketplace/tenants/${tenantId}/errors`).catch(() => ({ data: { errors: [] } })),
      api.get(`/connectors/${tenantId}/credentials`).catch(() => ({ data: { credentials: [] } })),
      api.get('/connectors/templates').catch(() => ({ data: { templates: [] } })),
    ]).then(([errorsRes, credsRes, templatesRes]) => {
      setAgentErrors(errorsRes.data.errors || []);
      setTenantCredentials(credsRes.data.credentials || []);
      setConnectorTemplates(templatesRes.data.templates || templatesRes.data || []);
    });
  };


  const openInstall = (agent) => {
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    setInstalling(agent);
    setFormData({ instanceName: `${agent.name}-${rand}`, enabledActions: [] });
  };
  const openConfig = (agent, initialTab = 'identity') => {
    setConfiguringInitialTab(initialTab);
    setConfiguring(agent);
    const tenantPublicSlug = tenantPublicSlugRef.current || '';
    setFormData({
      instanceName: agent.instanceName || agent.name,
      customPrompt: agent.basePrompt
        ? agent.systemPrompt?.replace(agent.basePrompt, '').replace(/^\n+/, '').trim()
        : agent.systemPrompt || '',
      basePrompt: agent.basePrompt || '',
      temperature: agent.temperature ?? 0.7,
      model: agent.model || '',
      enabledActions: agent.enabledActions || [],
      maxTokensPerSession: agent.maxTokensPerSession || '',
      sessionLimitMessage: agent.sessionLimitMessage || '',
      sessionTimeoutMinutes: agent.sessionTimeoutMinutes || '',
      contextWindowSize: agent.contextWindowSize || '',
      maxIterations: agent.maxIterations || '',
      useKnowledgeBase: agent.useKnowledgeBase !== false,
      usePromptCache: agent.usePromptCache === true,
      mediaHandling: agent.mediaHandling || {},
      publicSlug: tenantPublicSlug,
      agentPublicSlug: agent.publicSlug || '',
      welcomeMessage: agent.welcomeMessage || '',
      primaryColor: agent.primaryColor || '',
      instanceId: agent.instanceId || '',
    });
  };

  const handleInstall = async () => {
    try {
      const res = await api.post(`/marketplace/tenants/${tenantId}/agents/install`, { agentId: installing.agentId || installing.id, instanceName: formData.instanceName, customPrompt: formData.customPrompt, temperature: parseFloat(formData.temperature), model: formData.model, enabledActions: formData.enabledActions, lang: i18n.language });
      setInstalling(null);
      await loadAll();
      // Avisa se há conectores criados automaticamente aguardando credenciais
      const agent = res.data?.agent;
      if (agent) {
        const connectorsRes = await api.get(`/connectors/${tenantId}`).catch(() => ({ data: { connectors: [] } }));
        const pending = (connectorsRes.data.connectors || []).filter(c => c.agentInstanceId === agent.instanceId && c.fromTemplate && !c.enabled);
        if (pending.length > 0) {
          toast.warning(t('installSuccessWithPending', { count: pending.length, defaultValue: `Agente instalado! ${pending.length} conector(es) aguardam configuração de credenciais.` }));
        } else {
          toast.success(t('installSuccess', { defaultValue: 'Agente instalado com sucesso!' }));
        }
      } else {
        toast.success(t('installSuccess', { defaultValue: 'Agente instalado com sucesso!' }));
      }
    } catch (e) { toast.error(t('installError', { msg: e.response?.data?.message || e.message })); }
  };

  const handleUpdate = async () => {
    try {
      const agentPayload = {
        instanceName: formData.instanceName,
        customPrompt: formData.customPrompt,
        temperature: parseFloat(formData.temperature),
        model: formData.model,
        enabledActions: formData.enabledActions,
        useKnowledgeBase: formData.useKnowledgeBase !== false,
        welcomeMessage: formData.welcomeMessage || null,
        primaryColor: formData.primaryColor || null,
        agentPublicSlug: formData.agentPublicSlug || null,
        mediaHandling: formData.mediaHandling || null,
        // usePromptCache e sessionTimeoutMinutes só são enviados por sallus_admin
        // (backend também valida). Timeout: número em minutos; vazio = mantém/default.
        ...(isSallusAdmin ? {
          usePromptCache: formData.usePromptCache === true,
          ...(formData.sessionTimeoutMinutes !== '' && formData.sessionTimeoutMinutes != null
            ? { sessionTimeoutMinutes: parseInt(formData.sessionTimeoutMinutes, 10) }
            : {}),
          ...(formData.contextWindowSize !== '' && formData.contextWindowSize != null
            ? { contextWindowSize: parseInt(formData.contextWindowSize, 10) }
            : {}),
          ...(formData.maxIterations !== '' && formData.maxIterations != null
            ? { maxIterations: parseInt(formData.maxIterations, 10) }
            : {}),
        } : {}),
      };
      const slugToSave = formData.publicSlug || tenantId;
      await Promise.all([
        api.put(`/marketplace/tenants/${tenantId}/agents/${configuring.instanceId || configuring.agentId}`, agentPayload),
        api.put(`/tenants/${tenantId}`, { publicSlug: slugToSave }),
      ]);
      setConfiguring(null); loadAll();
    } catch (e) {
      const raw = e.response?.data?.message || e.response?.data?.error || e.message;
      const msg = typeof raw === 'string' ? raw : raw?.message || JSON.stringify(raw);
      toast.error(t('updateError', { msg }));
    }
  };

  const handleUninstall = async (agentId) => {
    const ok = await confirm({ title: t('uninstallConfirm'), danger: true });
    if (!ok) return;
    setUninstallingId(agentId);
    try { await api.delete(`/marketplace/tenants/${tenantId}/agents/${agentId}`); loadAll(); toast.success(t('uninstallSuccess')); }
    catch (e) { toast.error(t('uninstallError', { msg: e.response?.data?.message || e.message })); }
    finally { setUninstallingId(null); }
  };

  const resolveError = async (errorId) => {
    try {
      await api.delete(`/marketplace/tenants/${tenantId}/errors`, { data: { errorId } });
      setAgentErrors(prev => prev.filter(e => e.SK !== `AGENT_ERROR#${errorId}`));
    } catch (e) { toast.error(t('resolveError', { defaultValue: 'Erro ao resolver alerta' })); }
  };

  const resolveAllErrors = async () => {
    try {
      await api.delete(`/marketplace/tenants/${tenantId}/errors`, { data: { all: true } });
      setAgentErrors([]);
    } catch (e) { toast.error(t('resolveAllError', { defaultValue: 'Erro ao resolver alertas' })); }
  };

  if (loading) return (
    <>
      <AppHeader title={t('title')} />
      <LoadingContainer><Spinner size="24px" /> {t('loading', { ns: 'common' })}</LoadingContainer>
    </>
  );

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle', { tenantId, count: installed.length })}</PageSubtitle>
          </div>
        </PageHeader>

        <TabBar>
          <Tab active={activeTab === 'agents'} onClick={() => setActiveTab('agents')}>
            <Bot size={14} /> {t('installed')} ({installed.length})
          </Tab>
          <Tab active={activeTab === 'catalog'} onClick={() => setActiveTab('catalog')}>
            <Plus size={14} /> {t('availableToInstall')} ({available.filter(a => a.enabled !== false).length})
          </Tab>
          {activeTab === 'catalog' && (
            <CatalogFilters>
              <AvailSearch
                placeholder={t('searchAgent', { defaultValue: 'Buscar agente...' })}
                value={availSearch}
                onChange={e => setAvailSearch(e.target.value)}
              />
              <AvailSelect value={availCategory} onChange={e => setAvailCategory(e.target.value)}>
                <option value="">{t('allCategories', { defaultValue: 'Todas as categorias' })}</option>
                {[...new Set(available.map(a => a.category).filter(Boolean))]
                  .sort((a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b))
                  .map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
              </AvailSelect>
              <AvailSelect value={availTag} onChange={e => setAvailTag(e.target.value)}>
                <option value="">{t('filterByTag', { defaultValue: 'Tag...' })}</option>
                {[...new Set(available.flatMap(a => a.tags || []))].sort()
                  .map(tag => <option key={tag} value={tag}>{tag}</option>)}
              </AvailSelect>
              {(availSearch || availCategory || availTag) && (
                <ClearBtn onClick={() => { setAvailSearch(''); setAvailCategory(''); setAvailTag(''); }}>✕</ClearBtn>
              )}
            </CatalogFilters>
          )}
        </TabBar>

        {agentErrors.length > 0 && (
          <ErrorPanel>
            <ErrorPanelHeader>
              <AlertCircle size={14} color={theme.colors.warning} />
              <span>{t('execAlerts', { count: agentErrors.length, defaultValue: '{{count}} alertas de execução' })}</span>
              {agentErrors.length > 1 && (
                <Button variant="ghost" size="sm" style={{ marginLeft: 'auto' }} onClick={resolveAllErrors}>
                  {t('resolveAll', { defaultValue: 'Resolver todos' })}
                </Button>
              )}
            </ErrorPanelHeader>
            {agentErrors.map(err => {
              const errorId = err.SK?.replace('AGENT_ERROR#', '');
              const agent = installed.find(a => a.instanceId === err.agentId);
              return (
                <ErrorItem key={err.SK}>
                  <ErrorItemBody>
                    <ErrorItemTitle>
                      {agent ? agent.name : err.agentId || t('agentFallback', { defaultValue: 'Agente' })}
                      {err.tool && <ErrorTag>{err.tool}</ErrorTag>}
                      {err.connectorType && <ErrorTag>{err.connectorType}</ErrorTag>}
                    </ErrorItemTitle>
                    <ErrorItemMsg>{typeof err.message === 'string' ? err.message : err.message?.message || JSON.stringify(err.message)}</ErrorItemMsg>
                    <ErrorItemDate>{new Date(err.createdAt).toLocaleString('pt-BR')}</ErrorItemDate>
                  </ErrorItemBody>
                  <Button variant="ghost" size="sm" onClick={() => resolveError(errorId)}>{t('resolve', { defaultValue: 'Resolver' })}</Button>
                </ErrorItem>
              );
            })}
          </ErrorPanel>
        )}
        {activeTab === 'agents' && (
          <>
            <SectionTitle>{t('installed')}</SectionTitle>
            {installed.length === 0 ? (
              <EmptyState>
                <EmptyIcon><Bot size={48} strokeWidth={1} /></EmptyIcon>
                <EmptyText>{t('noInstalled')}</EmptyText>
              </EmptyState>
            ) : (
              <Grid min="280px" style={{ marginBottom: theme.spacing[8] }}>
                {installed.map(agent => {
                  const agentConnectors = connectorsByAgent[agent.instanceId] || [];
                  const pendingSetup = agentConnectors.filter(c => c.fromTemplate && !c.enabled);
                  const hasNoConnectors = agentConnectors.filter(c => c.type !== 'internal' && c.type !== 'memory').length === 0;
                  const needsAttention = pendingSetup.length > 0 || hasNoConnectors;
                  return (
                  <Card key={agent.instanceId || agent.agentId}>
                    <Flex justify="space-between" style={{ marginBottom: theme.spacing[3] }}>
                      <Flex gap={theme.spacing[3]}>
                        <AgentEmoji><BrandIcon icon={agent.icon || '🤖'} size={32} /></AgentEmoji>
                        <div>
                          <AgentName>{agent.instanceName || agent.name}</AgentName>
                          <AgentRole>{agent.role}</AgentRole>
                          {agent.instanceId && agent.agentId && agent.instanceId !== agent.agentId && (
                            <AgentBase>{t('baseLabel')}: {agent.agentId}</AgentBase>
                          )}
                        </div>
                      </Flex>
                      <Badge variant={needsAttention ? 'warning' : 'success'}>
                        {needsAttention ? '⚠️ ' + t('setupPending', { defaultValue: 'Configurar' }) : t('active', { ns: 'common' })}
                      </Badge>
                    </Flex>

                    {/* Guia de próximos passos — aparece enquanto há conectores pendentes ou nenhum conector */}
                    {needsAttention && (
                      <SetupGuide>
                        <SetupGuideTitle>
                          <AlertCircle size={13} color={theme.colors.warning} />
                          {t('setupGuideTitle', { defaultValue: 'Para o agente funcionar:' })}
                        </SetupGuideTitle>
                        {hasNoConnectors && pendingSetup.length === 0 && (
                          <SetupStep onClick={() => openConfig(agent, 'connectors')}>
                            <SetupStepNum>1</SetupStepNum>
                            <SetupStepText>{t('setupAddConnector', { defaultValue: 'Adicione um canal (WhatsApp, Email, Webchat...)' })}</SetupStepText>
                            <SetupStepArrow>→</SetupStepArrow>
                          </SetupStep>
                        )}
                        {pendingSetup.map((c, i) => (
                          <SetupStep key={c.connectorId} onClick={() => openConfig(agent, 'connectors')}>
                            <SetupStepNum>{i + 1}</SetupStepNum>
                            <SetupStepText>
                              {c.name || c.type} — {t('setupCredentials', { defaultValue: 'configure as credenciais' })}
                            </SetupStepText>
                            <SetupStepArrow>→</SetupStepArrow>
                          </SetupStep>
                        ))}
                      </SetupGuide>
                    )}

                    <PromptPreview>{agent.systemPrompt?.slice(0, 80)}...</PromptPreview>
                    <MetaRow>
                      <MetaTag>🌡 {agent.temperature}</MetaTag>
                      <MetaTag>🧠 {agent.model?.split('.').pop()?.split('-')[0]}</MetaTag>
                      {agent.creditsPerMessage != null && <MetaTag>💰 {agent.creditsPerMessage} {t('creditsPerMessage')}</MetaTag>}
                    </MetaRow>
                    {agent.instanceId && (
                      <InstanceIdRow title={t('instanceIdTooltip', { defaultValue: 'ID da instância — use no prompt para transferências' })}>
                        <span>ID:</span>
                        <InstanceIdValue>{agent.instanceId}</InstanceIdValue>
                        <CopyIdBtn aria-label={t('copy', { defaultValue: 'Copiar' })} onClick={() => { navigator.clipboard.writeText(agent.instanceId); }} title={t('copyId', { defaultValue: 'Copiar ID' })}>⧉</CopyIdBtn>
                      </InstanceIdRow>
                    )}
                    {agent.publicSlug && agent.instanceId && (
                      <PublicLinkRow>
                        <BrandIcon icon={agent.icon || '🤖'} size={12} />
                        <PublicLinkValue>
                          {`libbenagents.com/u/${tenantPublicSlugRef.current || tenantId}/${agent.publicSlug}`}
                        </PublicLinkValue>
                        <CopyIdBtn
                          onClick={() => navigator.clipboard.writeText(`${window.location.origin}/u/${tenantPublicSlugRef.current || tenantId}/${agent.publicSlug}`)}
                          title={t('copyPublicLink', { defaultValue: 'Copiar link público' })}
                        >⧉</CopyIdBtn>
                      </PublicLinkRow>
                    )}
                    {!needsAttention && agentConnectors.filter(c => c.type !== 'internal' && c.type !== 'memory').length > 0 && (
                      <ConnectorList>
                        {agentConnectors.filter(c => c.type !== 'internal' && c.type !== 'memory').map(c => (
                          <ConnectorChip key={c.connectorId} $enabled={c.enabled}>
                            🔌 {c.name} <span style={{ opacity: 0.6 }}>({c.type})</span>
                          </ConnectorChip>
                        ))}
                      </ConnectorList>
                    )}
                    <Flex gap={theme.spacing[2]} style={{ marginTop: theme.spacing[3] }}>
                      <Button variant="secondary" size="sm" style={{ flex: 1 }} onClick={() => openConfig(agent)}>
                        <Settings size={12} /> {t('configureAgent')}
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleUninstall(agent.instanceId || agent.agentId)} disabled={uninstallingId === (agent.instanceId || agent.agentId)}>
                        {uninstallingId === (agent.instanceId || agent.agentId) ? <Spinner size="12px" /> : <Trash2 size={12} />}
                      </Button>
                    </Flex>
                  </Card>
                  );
                })}
              </Grid>
            )}

          </>
        )}

        {/* ── Aba: Catálogo ── */}
        {activeTab === 'catalog' && (() => {
          const filtered = available.filter(a => {
            if (a.enabled === false) return false;
            const q = availSearch.toLowerCase();
            const matchSearch = !q || a.name?.toLowerCase().includes(q) || a.role?.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q);
            const matchCat = !availCategory || (a.category || '') === availCategory;
            const matchTag = !availTag || (a.tags || []).includes(availTag);
            return matchSearch && matchCat && matchTag;
          });
          const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
            const items = filtered.filter(a => (a.category || 'geral') === cat);
            if (items.length) acc[cat] = items;
            return acc;
          }, {});
          filtered.forEach(a => {
            const cat = a.category || 'geral';
            if (!CATEGORY_ORDER.includes(cat)) {
              grouped[cat] = grouped[cat] || [];
              if (!grouped[cat].find(x => x.agentId === a.agentId)) grouped[cat].push(a);
            }
          });
          return (
            <>
              {filtered.length === 0 ? (
                <EmptyState>
                  <EmptyIcon><Bot size={40} strokeWidth={1} /></EmptyIcon>
                  <EmptyText>{t('noAgentsFound', { defaultValue: 'Nenhum agente encontrado' })}</EmptyText>
                </EmptyState>
              ) : (
                Object.entries(grouped).map(([cat, agents]) => (
                  <AvailGroup key={cat}>
                    <AvailGroupTitle>{CATEGORY_LABELS[cat] || cat}</AvailGroupTitle>
                    <Grid min="280px">
                      {agents.map(agent => {
                        return (
                          <AvailableCard
                            key={agent.agentId || agent.id}
                            onClick={() => openInstall(agent)}
                          >
                            <Flex justify="space-between" style={{ marginBottom: theme.spacing[2] }}>
                              <Flex gap={theme.spacing[3]}>
                                <AgentEmoji><BrandIcon icon={agent.icon || '🤖'} size={32} /></AgentEmoji>
                                <div><AgentName>{agent.name}</AgentName><AgentRole>{agent.role}</AgentRole></div>
                              </Flex>
                            </Flex>
                            <AgentDesc>{agent.description}</AgentDesc>
                            {agent.creditsPerMessage != null && (
                              <MetaRow style={{ marginBottom: theme.spacing[2] }}>
                                <MetaTag>💰 {agent.creditsPerMessage} {t('creditsPerMessage')}</MetaTag>
                              </MetaRow>
                            )}
                            <Button variant="primary" size="sm" style={{ width: '100%' }} onClick={e => { e.stopPropagation(); openInstall(agent); }}>
                              <Plus size={12} /> {t('installAgent')}
                            </Button>
                          </AvailableCard>
                        );
                      })}
                    </Grid>
                  </AvailGroup>
                ))
              )}
            </>
          );
        })()}

      </PageContainer>


      {installing && (
        <ModalOverlay onClick={() => setInstalling(null)}>
          <ModalBox maxWidth="480px" onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <BrandIcon icon={installing.icon || '🤖'} size={22} style={{ marginRight: 8 }} />
                {installing.name}
              </ModalTitle>
              <Button variant="ghost" size="sm" onClick={() => setInstalling(null)}>✕</Button>
            </ModalHeader>
            <ModalBody>
              <AgentDesc style={{ marginBottom: theme.spacing[4] }}>{installing.description}</AgentDesc>
              <FormGroup>
                <Label>{t('instanceName')}</Label>
                <NameInput
                  value={formData.instanceName || ''}
                  onChange={e => setFormData(f => ({ ...f, instanceName: e.target.value }))}
                  placeholder={t('instanceNamePlaceholder')}
                  autoFocus
                />
                <FieldHint>{t('instanceNameHint', { defaultValue: 'Você pode renomear depois nas configurações.' })}</FieldHint>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setInstalling(null)}>{t('cancel', { ns: 'common' })}</Button>
              <Button variant="primary" onClick={handleInstall} disabled={!formData.instanceName?.trim()}>
                {t('installAgent')}
              </Button>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {configuring && (
        <ModalOverlay onClick={() => setConfiguring(null)}>
          <ModalBox maxWidth="900px" onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('configureAgent')} <BrandIcon icon={configuring.icon || '🤖'} size={20} style={{ margin: '0 4px' }} /> {configuring.name}</ModalTitle>
              <Button variant="ghost" size="sm" onClick={() => setConfiguring(null)}>✕</Button>
            </ModalHeader>
            <ModalBody><AgentForm formData={formData} setFormData={setFormData} isConfiguring isSallusAdmin={isSallusAdmin} agentInstanceId={configuring?.instanceId || configuring?.agentId} agentInstanceName={formData.instanceName || configuring?.name} tenantId={tenantId} allConnectorTypes={allConnectorTypes} agentConnectors={connectorsByAgent[configuring?.instanceId || configuring?.agentId] || []} onConnectorsChange={loadAll} tenantCredentials={tenantCredentials} onCredentialsChange={loadAll} templates={connectorTemplates} initialTab={configuringInitialTab} mediaFeatures={mediaFeatures} /></ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={() => setConfiguring(null)}>{t('cancel', { ns: 'common' })}</Button>
              <Button variant="primary" onClick={handleUpdate}>{t('save', { ns: 'common' })}</Button>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

    </>
  );
};

const TabBar = styled.div`display: flex; align-items: center; gap: 0; margin-bottom: ${theme.spacing[6]}; border-bottom: 2px solid ${theme.colors.border}; flex-wrap: wrap;`;
const Tab = styled.button`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[3]} ${theme.spacing[5]}; background: none; border: none; border-bottom: 3px solid ${p => p.active ? theme.colors.primary : 'transparent'}; color: ${p => p.active ? theme.colors.primary : theme.colors.textSecondary}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; cursor: pointer; margin-bottom: -2px; white-space: nowrap; &:hover { color: ${theme.colors.primary}; }`;
const CatalogFilters = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; margin-left: auto; padding-bottom: ${theme.spacing[1]}; flex-wrap: wrap;`;
const AgentBase = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; font-style: italic;`;
const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
// SectionTitle — importado de ui.js
const AgentEmoji = styled.div`font-size: 32px; line-height: 1; flex-shrink: 0;`;
const AgentName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const AgentRole = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const AgentDesc = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-bottom: ${theme.spacing[3]}; flex: 1;`;
const PromptPreview = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; font-style: italic; margin-bottom: ${theme.spacing[3]}; line-height: 1.4;`;
const MetaRow = styled.div`display: flex; gap: ${theme.spacing[2]};`;
const MetaTag = styled.span`font-size: ${theme.typography.xs}; background: ${theme.colors.gray50}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.full}; padding: 2px ${theme.spacing[2]};`;
const InstanceIdRow = styled.div`display: flex; align-items: center; gap: 4px; margin-top: 4px; font-size: 10px; color: ${theme.colors.textMuted};`;
const InstanceIdValue = styled.span`font-family: monospace; color: ${theme.colors.textSecondary};`;
const CopyIdBtn = styled.button`background: none; border: none; cursor: pointer; padding: 0 2px; color: ${theme.colors.textMuted}; font-size: 11px; &:hover { color: ${theme.colors.primary}; }`;
const PublicLinkRow = styled.div`display: flex; align-items: center; gap: 4px; margin-top: 2px; font-size: 10px; color: ${theme.colors.purple600};`;
const PublicLinkValue = styled.span`font-family: monospace; color: ${theme.colors.purple600}; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const AvailableCard = styled(Card)`
  display: flex; flex-direction: column; border-style: dashed;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  border-color: ${p => p.$locked ? theme.colors.warningBorder : p.$disabled ? theme.colors.border : theme.colors.border};
  background: ${p => p.$disabled ? theme.colors.gray50 : theme.colors.white};
  transition: box-shadow 0.15s, border-color 0.15s;
  &:hover { ${p => !p.$disabled && `box-shadow: ${theme.shadows.md}; border-color: ${p.$locked ? theme.colors.warning : theme.colors.primary};`} }
`;
const AvailGroup = styled.div`margin-bottom: ${theme.spacing[8]};`;
const AvailGroupTitle = styled.h3`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[4]} 0; padding-bottom: ${theme.spacing[2]}; border-bottom: 2px solid ${theme.colors.border};`;
const AvailSearch = styled.input`padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.lg}; font-size: ${theme.typography.sm}; outline: none; width: 180px; &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }`;
const AvailSelect = styled.select`padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.lg}; font-size: ${theme.typography.sm}; outline: none; background: white; cursor: pointer; &:focus { border-color: ${theme.colors.primary}; }`;
const ClearBtn = styled.button`padding: ${theme.spacing[1]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.full}; background: white; font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; cursor: pointer; &:hover { border-color: ${theme.colors.danger}; color: ${theme.colors.danger}; }`;
const ConnectorList = styled.div`display: flex; flex-wrap: wrap; gap: ${theme.spacing[1]}; margin: ${theme.spacing[2]} 0;`;
const ConnectorChip = styled.div`font-size: ${theme.typography.xs}; padding: 2px ${theme.spacing[2]}; border-radius: ${theme.radii.full}; border: 1px solid ${p => p.$needsSetup ? theme.colors.warningBorder : p.$enabled ? theme.colors.primary : theme.colors.border}; color: ${p => p.$needsSetup ? '#92400e' : p.$enabled ? theme.colors.primary : theme.colors.textMuted}; background: ${p => p.$needsSetup ? theme.colors.warningLight : p.$enabled ? theme.colors.primaryLight : theme.colors.gray50};`;
const SetupWarning = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]}; font-size: ${theme.typography.xs}; color: #92400e; background: ${theme.colors.warningLight}; border: 1px solid ${theme.colors.warningBorder}; border-radius: ${theme.radii.md}; padding: ${theme.spacing[2]} ${theme.spacing[3]}; margin-top: ${theme.spacing[2]};`;

// ── Setup Guide ───────────────────────────────────────────────────────────────

const SetupGuide = styled.div`
  background: ${theme.colors.warningLight};
  border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const SetupGuideTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SetupStep = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: white;
  border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.md};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { border-color: ${theme.colors.primary}; background: ${theme.colors.primaryLight}; }
  &:focus-visible { outline: 2px solid ${theme.colors.primary}; outline-offset: 2px; }
`;

const SetupStepNum = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${theme.colors.warning};
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SetupStepText = styled.div`
  flex: 1;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textPrimary};
`;

const SetupStepArrow = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

// ── Install modal ─────────────────────────────────────────────────────────────

const NameInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;
// FieldHint — importado de ui.js
// SectionTitle — importado de ui.js

const ErrorPanel = styled.div`
  border: 1.5px solid ${theme.colors.warning};
  border-radius: ${theme.radii.lg};
  background: #fffbeb;
  margin-bottom: ${theme.spacing[5]};
  overflow: hidden;
`;
const ErrorPanelHeader = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: #fef3c7;
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.warning};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;
const ErrorItem = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-top: 1px solid #fde68a;
`;
const ErrorItemBody = styled.div`flex: 1; min-width: 0;`;
const ErrorItemTitle = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: 2px;`;
const ErrorTag = styled.span`font-size: 10px; background: #fde68a; border-radius: ${theme.radii.sm}; padding: 1px 6px; color: #92400e;`;
const ErrorItemMsg = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.danger}; margin-bottom: 2px;`;
const ErrorItemDate = styled.div`font-size: 10px; color: ${theme.colors.textMuted};`;

export default TenantAgents;
